import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import React, { useEffect, useReducer } from 'react';

/** MUI */
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';

/** Hooks */
import useCurrency from 'hooks/useCurrency';
import useResponsive from 'hooks/useResponsive';

/** Components */

/** Const  */
import currencies from 'constants/currencies';

import { Tooltip } from '@mui/material';
import { useCheckout } from 'provider/CheckoutProvider';
import { log } from 'utils/functions';

const bootstrapDialogTitleDefaultPropChildren: React.ReactNode = null;
interface BootstrapDialogTitleProps {
  children?: React.ReactNode;
  onClose: () => void;
  styles?: React.CSSProperties;
  iconStyle?: React.CSSProperties;
  [key: string]: any;
}

const BootstrapDialogTitle = ({
  children = bootstrapDialogTitleDefaultPropChildren,
  onClose,
  styles,
  iconStyle,
  ...other
}: BootstrapDialogTitleProps) => {
  return (
    <DialogTitle sx={{ m: 0, position: 'relative', ...styles }} {...other}>
      {children}
      {onClose ? (
        <IconButton aria-label="close" onClick={onClose} sx={{ position: 'absolute', top: 8, right: 8 }}>
          <CloseIcon sx={{ ...iconStyle }} />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
};

BootstrapDialogTitle.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func.isRequired,
};

const reducerActions = {
  UPDATE_CURRENCY: 'update_currency',
  UPDATE_OPEN: 'update_open',
  UPDATE_MANY: 'update_all',
};

interface CurrencyPickerState {
  selectedCurrency: string;
  open: boolean;
}

type CurrencyPickerAction =
  | { type: typeof reducerActions.UPDATE_CURRENCY; selectedCurrency: string }
  | { type: typeof reducerActions.UPDATE_OPEN; open: boolean }
  | { type: typeof reducerActions.UPDATE_MANY; payload: Partial<CurrencyPickerState> };

const initialState: CurrencyPickerState = {
  selectedCurrency: '',
  open: false,
};

const CurrencyPickerReducer = (state: CurrencyPickerState, action: CurrencyPickerAction): CurrencyPickerState => {
  switch (action.type) {
    case reducerActions.UPDATE_CURRENCY:
      return { ...state, selectedCurrency: (action as { selectedCurrency: string }).selectedCurrency };
    case reducerActions.UPDATE_OPEN:
      return { ...state, open: (action as { open: boolean }).open };
    case reducerActions.UPDATE_MANY:
      return { ...state, ...(action as { payload: Partial<CurrencyPickerState> }).payload };
    default:
      return state;
  }
};

const CurrencyPicker = () => {
  const isMobile = useResponsive('down', 'sm');
  const { enqueueSnackbar } = useSnackbar();
  const { currency, setCurrency } = useCurrency();
  const [{ selectedCurrency, open }, dispatch] = useReducer(CurrencyPickerReducer, initialState);
  const { loading } = useCheckout();
  const changeCurrencyButtonTitle = `Change Currency${loading ? ' (Loading...)' : ''}`;

  useEffect(() => {
    dispatch({
      type: reducerActions.UPDATE_MANY,
      payload: { selectedCurrency: currency?.name },
    });
  }, [currency?.name]);

  const handleClickOpen = () => {
    dispatch({
      type: reducerActions.UPDATE_OPEN,
      open: true,
    });
  };

  const handleClose = () => {
    dispatch({
      type: reducerActions.UPDATE_OPEN,
      open: false,
    });
  };

  interface HandleChangeCurrencyEvent {
    target: {
      value: string;
    };
  }

  const handleChangeCurrency = (event: HandleChangeCurrencyEvent) => {
    dispatch({ type: reducerActions.UPDATE_CURRENCY, selectedCurrency: event.target.value });
  };

  const handleSelection = () => {
    try {
      setCurrency(selectedCurrency);
      enqueueSnackbar('Changes sucessful', {
        variant: 'success',
      });
      handleClose();
    } catch (e) {
      log.error(e);
      enqueueSnackbar(`Could not save your changes. Refresh the page and try again.`, {
        variant: 'error',
      });
    }
  };

  return (
    <>
      <Box onClick={!loading ? handleClickOpen : () => {}} sx={{ display: 'inline-block', color: 'secondary.main' }}>
        <Tooltip title={changeCurrencyButtonTitle}>
          <Button
            variant="text"
            size="small"
            disabled={loading}
            sx={{
              textTransform: 'none',
              fontWeight: 'bold',
              fontSize: isMobile ? '12px' : '0.9rem',
              minWidth: 'unset',
            }}
            data-testid="currencySymbolTest"
          >
            {currency.symbol} - {currency.name}
          </Button>
        </Tooltip>
      </Box>
      <Dialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        sx={{ padding: isMobile ? '16px' : '24px', '.MuiPaper-root': { minWidth: '20rem' } }}
        data-testid="currency-dialog"
      >
        <BootstrapDialogTitle
          id="customized-dialog-title"
          onClose={handleClose}
          styles={{
            padding: isMobile ? '1.5rem 1.5rem 1rem' : '2rem 4rem 1rem 2rem',
            fontSize: isMobile ? '18px' : '1.3rem',
          }}
          iconStyle={{ width: isMobile ? '16px' : '28px', height: isMobile ? '16px' : '28px' }}
        >
          Select your currency
        </BootstrapDialogTitle>
        <DialogContent sx={{ padding: '0 2rem 0 !important' }}>
          <Box sx={{ minWidth: 120, mt: '1rem' }}>
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">Currency</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={selectedCurrency}
                label="Currency"
                onChange={handleChangeCurrency}
              >
                {currencies.map((c) => (
                  <MenuItem key={c.name} value={c.name}>
                    {c.symbol} - {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleClose}>
            Cancel
          </Button>
          <Button autoFocus onClick={handleSelection}>
            Save changes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CurrencyPicker;
